import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import {
  Button,
  Icon,
  IconType,
  Loader,
  QueryBoundary,
  Size,
} from '@outdoria/ui';
import {
  CDN_URL,
  useAdminDeleteItem,
  useAdminGetAllItems,
  useGetItemTypesAll,
} from '@outdoria/api';
import type { Item } from '@outdoria/types';
import { ActionBar, Table, TableHeader } from '../../containers';
import { Form } from '../../components';

import s from './ItemsView.module.scss';

export const ItemsView = () => (
  <QueryBoundary fallback={<Loader />}>
    <Load />
  </QueryBoundary>
);

const Load = () => {
  const navigate = useNavigate();

  const { data: items } = useAdminGetAllItems();
  const { tableHeaders, tableRows } = useTable(items);

  const onItemCreate = () => {
    navigate('create');
  };

  return (
    <div className={s.view}>
      <ActionBar
        buttons={<Button label="Create new item" onClick={onItemCreate} />}
      />

      <Table tableHeaders={tableHeaders} items={tableRows} />
    </div>
  );
};

const useTable = (items: Item[]) => {
  const { data: itemTypes } = useGetItemTypesAll();

  const tableHeaders: TableHeader<Item>[] = [
    { id: 'name', label: 'Name' },
    { id: 'item_type_id', label: 'Item type' },
    { id: 'rarity_id', label: 'Rarity' },
    { id: 'description', label: 'Description' },
    { id: 'is_rotable', label: 'Rotable?' },
    { id: 'weight', label: 'Weight' },
    { id: 'stack_size', label: 'Stack size' },
    { id: 'actions', label: 'Actions' },
  ];

  const tableRows = items.map((item: Item) => [
    <Table.Cell
      content={
        <>
          <Form.Image src={`${CDN_URL}/${item.thumbnail}`} size={Size.SMALL} />
          {item.name}
        </>
      }
    />,
    <Table.Cell
      content={
        <>
          <Icon
            icon={
              itemTypes.find(
                itemType => itemType.item_type_id === item.item_type_id,
              )?.icon as IconType
            }
            size={Size.TINY}
          />
          {item.item_type_id?.toLocaleUpperCase()}
        </>
      }
    />,
    <Table.Cell content={item.rarity_id?.toLocaleUpperCase()} />,
    <Table.Cell content={item.description} />,
    <Table.Cell content={item.is_rotable} />,
    <Table.Cell content={item.weight} />,
    <Table.Cell content={item.stack_size} />,
    <Table.Cell content={<Actions id={item.item_id} />} />,
  ]);

  return { tableHeaders, tableRows };
};

const Actions = ({ id }: { id: string }) => {
  const navigate = useNavigate();
  const { mutate, error, isSuccess } = useAdminDeleteItem();

  useEffect(() => {
    if (isSuccess) toast.success('Yay! Deleted :3');
  }, [isSuccess]);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  const onDelete = () => {
    const shouldDelete = confirm('Do you really want to delete this item?');
    if (shouldDelete) mutate(id);
  };

  return (
    <>
      <Button
        label={<Icon icon="Edit" size={Size.MICRO} />}
        onClick={() => navigate(id)}
        style={{ width: '40px' }}
      />
      <Button
        label={<Icon icon="Trash" size={Size.MICRO} />}
        onClick={onDelete}
        variant={Button.Variant.DANGER}
        style={{ width: '40px' }}
      />
    </>
  );
};
